/**
 * useSelectedEntity
 *
 * SelectionContext migration.
 *
 * This hook is THE single source for full entity data.
 * It reads IDs from selectionStore, fetches data via React Query,
 * and returns a normalized entity — replacing what SelectionContext
 * previously fetched, shaped, and stored in React state.
 *
 * ─────────────────────────────────────────────────────
 * REPLACES these SelectionContext APIs:
 *
 *  selectedEntity                  → entity
 *  selectedEntity.isComplete       → isSuccess / hasRelationships
 *  selectedEntity.relatedMedia     → entity.relatedMedia
 *  selectedEntity.relationships    → entity.relationships
 *  selectedEntity.thumbnail        → entity.thumbnailUrl  ← single resolved value
 *  selectedEntity.primary_image    → entity.primaryImageUrl ← single resolved value
 *  loadingProgress                 → isLoading / isFetchingRelationships
 *  refreshSelectedEntity()         → refresh()
 *  fetchRelationshipsForSelected() → fetchRelationships()
 *  fetchAndSelectEntityRelationships() → fetchRelationships()
 * ─────────────────────────────────────────────────────
 *
 * Usage:
 *   const { entity, isLoading, fetchRelationships, refresh } = useSelectedEntity();
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getEntityQueryKey, useEntityFetch } from "../api/entity";
import { useConfig } from "../contexts/ConfigContext";
import { getCmTableRow } from "../services/cmTables";
import { useAuthSelector } from "../store/auth";
import { useSelectionActions, useSelectionSelector, type LoadingProgress } from "../store/selection";
import type { ApiEndpoint, Entity, ICmTableDataMediaItem, INormalisedRelations, MediaItemDetailed } from "../types";
import { isCmTableEndpoint, getCmTableSlugFromEndpoint, getCmTableRowQueryKey } from "../utilities/cmTableEndpoint";

// ─── Normalized entity type ───────────────────────────────────────────────────
// Components should depend on THIS shape, not the raw Entity from SelectionContext.

export interface SelectedEntity extends Entity {
  /** Single resolved primary image URL. Replaces the scattered thumbnail/primary_image reads. */
  primaryImageUrl: string | null;
  /** Single resolved thumbnail URL (lower res). */
  thumbnailUrl: string | null;
  /** True when base entity details are available (replaces entity.isComplete). */
  isDataLoaded: boolean;
  /** True when L3 relationships/media are available. */
  hasRelationships: boolean;
  relations?: INormalisedRelations;
}

// ─── Internal helpers (extracted from SelectionContext) ───────────────────────

/**
 * Resolves the best available image URL from raw API data.
 * Centralises logic that was scattered across:
 * SelectionContext, DataItemDetails, EntityMediaOverview, MediaDrawer.
 */
function resolvePrimaryImage(raw: Entity): { primaryImageUrl: string | null; thumbnailUrl: string | null } {
  // 1. Explicit primary_image object (Canonical / Django standard pattern)
  if (raw.primary_image?.url) {
    return {
      primaryImageUrl: raw.primary_image.url,
      thumbnailUrl: raw.primary_image.thumbnail ?? raw.primary_image.url
    };
  }

  // 2. Top-level thumbnail (Fulcrum fallback)
  if (raw.thumbnail) {
    return { primaryImageUrl: raw.thumbnail, thumbnailUrl: raw.thumbnail };
  }

  return { primaryImageUrl: null, thumbnailUrl: null };
}

/** Normalizes media items to a consistent shape regardless of data source. */
function normalizeMediaItem(media: ICmTableDataMediaItem | any): MediaItemDetailed {
  return {
    ...media,
    type: media.media_type || media.type || "photo",
    media_type: media.media_type || media.type || "photo",
    original_file: media.original_file ?? {
      filename: media.name || "unknown",
      type: media.media_type || media.type || "photo",
      size: "0"
    }
  };
}

/** Extracts media items from raw API data regardless of data source structure. */
function extractRelatedMedia(raw: Entity): MediaItemDetailed[] {
  // Fulcrum: fullMediaObjects nested inside data keys
  for (const key in raw as any) {
    const val = (raw as any)[key];
    if (val && typeof val === "object" && val.fullMediaObjects) {
      return val.fullMediaObjects.map(normalizeMediaItem);
    }
  }

  // Django: relationships.media array
  if (raw.relationships?.media && Array.isArray(raw.relationships.media)) {
    return raw.relationships.media.map(normalizeMediaItem);
  }

  // Flat media array (fallback)
  const flatMedia = (raw as any).media;
  if (flatMedia && Array.isArray(flatMedia)) {
    return flatMedia.map(normalizeMediaItem);
  }

  return [];
}

/** True when relationship buckets contain at least one item (empty objects are not "has relationships"). */
function entityHasPopulatedRelationships(raw: Entity): boolean {
  // Check normalized shape first
  if (raw.relations) {
    return Object.keys(raw.relations.byTableSlug).length > 0 || raw.relations.media.length > 0;
  }

  const rel = raw.relationships;
  if (!rel) return false;

  // V2 normalised shape
  if ((rel as any).byTableSlug) {
    return (
      Object.values((rel as any).byTableSlug).some((arr) => Array.isArray(arr) && (arr as any[]).length > 0) ||
      (Array.isArray(rel.media) && rel.media.length > 0)
    );
  }

  // V1 shape
  if (rel.related_entities && Object.values(rel.related_entities).some((arr) => Array.isArray(arr) && arr.length > 0)) {
    return true;
  }
  if (rel.geo_entities && Object.values(rel.geo_entities).some((arr) => Array.isArray(arr) && arr.length > 0)) {
    return true;
  }
  if (Array.isArray(rel.media) && rel.media.length > 0) {
    return true;
  }
  return false;
}

/**
 * Resolves the primary image as a rich object containing resolved V2 metadata
 * (id, name, description) from relations.media[].
 *
 * NOTE: This hook is intentionally the secondary enrichment site.
 * Because L2/L3 merge is done in normalizeEntity, we enrich the primary_image
 * object here using both raw primary image details and fully resolved media relationships.
 */
/** Merges L2 and L3 data, promoting resolved fields to top-level. */
function normalizeEntity(selectedEndpoint: ApiEndpoint | null, l2?: Entity, l3?: Entity): SelectedEntity {
  // l3 supersedes l2 when available (it contains everything l2 has, plus relationships)
  const raw = l3 ?? l2;
  if (!raw) {
    throw new Error("normalizeEntity: at least one data source (L2 or L3) must be provided");
  }
  const { primaryImageUrl, thumbnailUrl } = resolvePrimaryImage(raw);
  const relatedMedia = extractRelatedMedia(raw);

  return {
    // Spread raw entity fields first so nothing is lost
    ...raw,

    // Media now returns `geometry` as WKT (e.g. "POINT (85.3 27.7)"), matching
    // Place / FamilyArea / Trip — no special-casing needed.
    geometry: raw.geometry,

    // add layerId which is form endpoint
    layerId: selectedEndpoint?.mapLayerId,

    // Resolved image — single source of truth for all image reads
    primaryImageUrl,
    thumbnailUrl,

    primary_image: raw.primary_image,

    // Resolved media list — normalized, always an array
    relatedMedia,
    hasMedia: relatedMedia.length > 0,

    // Status flags — replaces isComplete boolean from SelectionContext
    isDataLoaded: true, // if we're here, base entity data is loaded (from L2 or L3)
    hasRelationships: entityHasPopulatedRelationships(raw)
  };
}

// ─── Return type ──────────────────────────────────────────────────────────────

export interface UseSelectedEntityResult {
  /** Normalized entity with resolved image URLs, media list, and status flags. */
  entity: SelectedEntity | undefined;

  /** The active endpoint (from selectionStore) */
  endpoint: ApiEndpoint | null;

  /** True while the primary data fetch (L2, or L3 if alwaysRequestRelatedItemsAndMedia is enabled) is in flight. */
  isLoading: boolean;

  /** True when primary data is loaded successfully. Replaces entity.isComplete. */
  isSuccess: boolean;

  /** True if the primary data fetch failed. */
  isError: boolean;

  /** True while L3 (relationships + media) fetch is in flight. */
  isFetchingRelationships: boolean;

  /**
   * Re-fetches L2 (and L3 if previously loaded) by invalidating the React Query cache.
   * Equivalent to: SelectionContext.refreshSelectedEntity()
   */
  refresh: () => Promise<void>;

  /**
   * Explicitly triggers the L3 (relationships + relatedMedia) fetch.
   * Returns `true` if relationships were found, `false` if the entity has none.
   * When `false`, the panel is auto-collapsed and the caller should show a toast.
   */
  fetchRelationships: () => Promise<boolean>;

  // step wise loading progress
  loadingProgress: LoadingProgress | null;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useSelectedEntity(): UseSelectedEntityResult {
  const { user: profile } = useAuthSelector();
  const config = useConfig();
  const allEndpoints = config.apiEndpoints || [];

  // IDs only — selectionStore is the source of truth
  const { selectedEntityId, selectedEndpoint, showRelationShip, loadingProgress } = useSelectionSelector();
  const { setShowRelationship, setLoadingProgress } = useSelectionActions();

  const queryClient = useQueryClient();

  const isEnabled = !!selectedEntityId && !!selectedEndpoint;

  // Tracks which selection we've already auto-opened the relationship panel for,
  // so a manual close ("Back to grid") isn't undone on the next render. Only the
  // alwaysRequestRelatedItemsAndMedia auto-open path uses this.
  const lastAutoOpenedSelectionKeyRef = useRef<string | null>(null);
  // Tracks the last committed hasData value for the current selection so we can
  // re-open the panel when the entity gains relationships after initially having
  // none (e.g. after the user creates a relationship via EntityForm).
  // null = not yet committed for this selection.
  const lastEntityHadDataRef = useRef<boolean | null>(null);

  const onProgress = useCallback(
    (step: string, current: number, total: number) => {
      setLoadingProgress({ step, current, total });
    },
    [setLoadingProgress]
  );

  // Reset progress when entity selection changes
  useEffect(() => {
    setLoadingProgress(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntityId, selectedEndpoint]);

  // Detect CM Table endpoints
  const isCmTable = isCmTableEndpoint(selectedEndpoint);

  // ── Re-derive cmTableEntity whenever the drawer-data list cache for this
  // table updates (e.g. after an edit invalidates/refetches the list), so
  // "Currently Viewing" / map selection reflect the latest name & geometry
  // without requiring selectedEntityId/selectedEndpoint to change.
  const [drawerDataVersion, setDrawerDataVersion] = useState(0);
  useEffect(() => {
    if (!isCmTable || !selectedEndpoint) return;
    const tableName = selectedEndpoint.tableName;
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      // Only react when the list query's DATA actually changed (a fresh successful
      // fetch result), e.g. after an edit invalidates/refetches the list.
      //
      // CRITICAL: do NOT react to every cache event. "observerOptionsUpdated"
      // fires on each render of the SideBar's useInfiniteQuery (its inline
      // queryFn/getNextPageParam closures are recreated every render). Bumping
      // state on that event re-renders all useSelectedEntity consumers, which
      // re-renders the SideBar, which emits another "observerOptionsUpdated" —
      // an infinite render loop that freezes the UI (the symptom: route URL
      // changes but the page never repaints).
      if (event?.type !== "updated" || (event as any)?.action?.type !== "success") return;
      const key = (event as any)?.query?.queryKey;
      if (Array.isArray(key) && key[0] === "drawer-data" && key[1] === tableName) {
        setDrawerDataVersion((v) => v + 1);
      }
    });
    return unsubscribe;
  }, [isCmTable, selectedEndpoint, queryClient]);

  // ── CM Table fast-path: read entity from the list cache, no extra API call ─
  const cmTableEntity = useMemo(() => {
    if (!isCmTable || !selectedEntityId || !selectedEndpoint) return undefined;

    // The sidebar's useInfiniteQuery caches under key ["drawer-data", tableName, searchTerm]
    // Scan all pages to find the selected entity id.
    const cacheEntry = queryClient.getQueriesData<{
      pages: Array<{ data: any[]; pagination: any }>;
    }>({ queryKey: ["drawer-data", selectedEndpoint.tableName] });

    for (const [, cached] of cacheEntry) {
      if (!cached?.pages) continue;
      for (const page of cached.pages) {
        const found = page?.data?.find((row: any) => String(row.id) === String(selectedEntityId));
        if (found) return found as Entity;
      }
    }
    return undefined;
  }, [isCmTable, selectedEntityId, selectedEndpoint, queryClient, drawerDataVersion]);

  // ── L2 Fallback: Fetch single CM Table row on cache miss ───────────────────
  const {
    data: rowFallbackData,
    isLoading: isFallbackLoading,
    isError: isFallbackError
  } = useQuery({
    queryKey: getCmTableRowQueryKey(getCmTableSlugFromEndpoint(selectedEndpoint) || selectedEndpoint?.tableName, selectedEntityId),
    queryFn: async () => {
      if (isCmTable && selectedEndpoint) {
        const slug = getCmTableSlugFromEndpoint(selectedEndpoint) || selectedEndpoint.tableName;
        const res = await getCmTableRow(slug, selectedEntityId as string, { include_relations: true });
        return res.data;
      }
      return null;
    },
    // Fetch on a list-cache MISS as before, and also whenever
    // alwaysRequestRelatedItemsAndMedia is on — the list cache is relation-free,
    // so the flag path needs the full row (include_relations=true) even on a hit.
    // React Query keys this by row, so all consumers share one deduped request.
    enabled: isEnabled && isCmTable && (!cmTableEntity || Boolean(config.alwaysRequestRelatedItemsAndMedia))
  });

  // ── L2: Full entity details ──────────────────────────────────────────────
  const {
    data: l2Data,
    isLoading,
    isSuccess,
    isError
  } = useEntityFetch(
    {
      entityId: selectedEntityId as string,
      selectedEndpoint,
      level: "L2",
      allEndpoints,
      userProfile: profile,
      onProgress
    },
    {
      // Skip fetch entirely for cm-table endpoints – data lives in the list cache
      enabled: isEnabled && !config.alwaysRequestRelatedItemsAndMedia && !isCmTable
    }
  );

  // ── L3: Relationships + relatedMedia (lazy by default) ───────────────────
  const {
    data: l3Data,
    isLoading: l3IsLoading,
    isSuccess: l3IsSuccess,
    isError: l3IsError,
    isFetching: isFetchingRelationships,
    refetch: refetchL3
  } = useEntityFetch(
    {
      entityId: selectedEntityId as string,
      selectedEndpoint,
      level: "L3",
      allEndpoints,
      userProfile: profile,
      onProgress
    },
    {
      // Skip L3 for cm-table – relations are already included in the list response
      enabled: isEnabled && (showRelationShip || !!config.alwaysRequestRelatedItemsAndMedia) && !isCmTable,
      staleTime: 30_000
    }
  );

  // ── Derived normalized entity — CM Table fast-path wins when applicable ──
  const entity = useMemo(() => {
    if (isCmTable) {
      // Prefer rowFallbackData when it carries relationships (fetched on demand via fetchRelationships).
      // Fall back to the list-cache entry which has no include_relations data.
      const raw = ((rowFallbackData as any)?.relationships ? rowFallbackData : cmTableEntity) as Entity | undefined;
      if (raw) return normalizeEntity(selectedEndpoint, raw, undefined);
      return undefined;
    }
    if (!l2Data && !l3Data) return undefined;
    return normalizeEntity(selectedEndpoint, l2Data, l3Data);
  }, [isCmTable, cmTableEntity, rowFallbackData, selectedEndpoint, l2Data, l3Data]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const refresh = useCallback(async () => {
    if (!selectedEntityId || !selectedEndpoint) return;

    await queryClient.invalidateQueries({
      queryKey: getEntityQueryKey(selectedEntityId, selectedEndpoint, "L2")
    });

    // If L3 was loaded before, refresh it too
    const l3Cached = queryClient.getQueryData(getEntityQueryKey(selectedEntityId, selectedEndpoint, "L3"));
    if (l3Cached) {
      await queryClient.invalidateQueries({
        queryKey: getEntityQueryKey(selectedEntityId, selectedEndpoint, "L3")
      });
    }
  }, [queryClient, selectedEntityId, selectedEndpoint]);

  /**
   * Explicitly triggers L3 fetch by setting showRelationShip = true.
   * - If L3 returns data: panel stays open.
   * - If L3 returns empty: panel auto-collapses and returns false so callers
   *   can show a "No related items found" message.
   */
  const fetchRelationships = useCallback(async (): Promise<boolean> => {
    if (!isEnabled) return false;

    setLoadingProgress(null);
    setShowRelationship(true);

    // CM Table: L3 is disabled for this path. Fetch the full row on demand and
    // write it into the fallback-row cache so the entity memo re-derives with
    // the relationship data.
    if (isCmTable && selectedEndpoint) {
      try {
        const slug = getCmTableSlugFromEndpoint(selectedEndpoint) || selectedEndpoint.tableName;
        const res = await getCmTableRow(slug, selectedEntityId as string, { include_relations: true });
        const fullRow = res.data;
        queryClient.setQueryData(
          getCmTableRowQueryKey(slug, selectedEntityId),
          fullRow
        );
        const rel = (fullRow as any)?.relationships;
        const hasData =
          !!(rel?.byTableSlug && Object.values(rel.byTableSlug).some((arr) => Array.isArray(arr) && (arr as any[]).length > 0)) ||
          (Array.isArray(rel?.media) && (rel.media as any[]).length > 0);
        if (!hasData) setShowRelationship(false);
        return hasData;
      } catch {
        setShowRelationship(false);
        return false;
      }
    }

    // Non-CM Table: use the L3 React Query path.
    const queryKey = getEntityQueryKey(selectedEntityId, selectedEndpoint, "L3");
    const cachedData = queryClient.getQueryData<Entity>(queryKey);

    // If L3 data is already present in cache, use it immediately.
    // Otherwise, trigger a refetch via refetchL3().
    let l3Result = cachedData;
    if (!l3Result) {
      const result = await refetchL3();
      l3Result = result.data;
    }

    // Check if L3 returned any relationship data (V2 format only).
    const hasData =
      !!(
        l3Result?.relationships?.byTableSlug &&
        Object.values(l3Result.relationships.byTableSlug).some((arr) => Array.isArray(arr) && arr.length > 0)
      ) ||
      (Array.isArray(l3Result?.relationships?.media) && l3Result.relationships.media.length > 0);

    if (!hasData) {
      // No relationships found — collapse panel, let caller handle the toast
      setShowRelationship(false);
    }

    return hasData;
  }, [isCmTable, refetchL3, isEnabled, setShowRelationship, setLoadingProgress, queryClient, selectedEntityId, selectedEndpoint]);

  // ── Config-driven panel state (alwaysRequestRelatedItemsAndMedia) ──────────
  // When the flag is on, drive the relationship panel from the loaded data —
  // ONCE per selection — so users never click "Find Related". The fetch is
  // declarative and deduped (L3 for non-CM; the include_relations fallback row
  // for CM), so reading state here triggers no duplicate requests.
  //
  // IMPORTANT: we wait for that fetch to SETTLE and open ONLY when the entity
  // actually has relationships or media. Opening optimistically and collapsing
  // on empty caused a visible open→close flicker on first selection of an entity
  // with no related items, so empty entities are simply never opened. The
  // per-selection ref guard lets a manual close ("Back to grid") stick.
  useEffect(() => {
    if (!config.alwaysRequestRelatedItemsAndMedia || !isEnabled || !entity) return;
    if (!selectedEntityId || !selectedEndpoint) return;

    const settled = isCmTable
      ? !isFallbackLoading && (cmTableEntity !== undefined || rowFallbackData !== undefined)
      : l3IsSuccess;
    if (!settled) return;

    const selectionKey = `${selectedEndpoint.tableName}:${String(selectedEntityId)}`;
    const hasData = Boolean(entity.hasRelationships || entity.hasMedia);

    // When the selection changes, reset per-entity tracking so the new entity
    // gets a clean slate.
    if (lastAutoOpenedSelectionKeyRef.current !== selectionKey) {
      lastAutoOpenedSelectionKeyRef.current = selectionKey;
      lastEntityHadDataRef.current = null;
    }

    // Skip if data state hasn't changed since we last committed — this prevents:
    //  1. Redundant setState calls on every re-render (no data → panel stays closed).
    //  2. Re-opening a panel the user just manually closed ("Back to grid") when the
    //     entity still has the same data it had before the close.
    // Transitions in either direction (false→true or true→false) do update the panel,
    // so adding or removing all relationships auto-shows/hides it correctly.
    if (lastEntityHadDataRef.current === hasData) return;
    lastEntityHadDataRef.current = hasData;

    // Open when there's something to show; otherwise ensure the panel is closed
    // (handles a panel left open by the previously selected entity).
    setShowRelationship(hasData);
  }, [
    config.alwaysRequestRelatedItemsAndMedia,
    isEnabled,
    entity,
    isCmTable,
    isFallbackLoading,
    cmTableEntity,
    rowFallbackData,
    l3IsSuccess,
    selectedEntityId,
    selectedEndpoint,
    setShowRelationship
  ]);

  return {
    entity,
    endpoint: selectedEndpoint,
    // For CM Table entities data comes from the list cache OR fallback fetch — no separate L2/L3 fetch fires,
    // so treat as immediately loaded (not loading, not error).
    isLoading: isCmTable ? !cmTableEntity && isFallbackLoading : config.alwaysRequestRelatedItemsAndMedia ? l3IsLoading : isLoading,
    isSuccess: isCmTable ? !!cmTableEntity || !!rowFallbackData : config.alwaysRequestRelatedItemsAndMedia ? l3IsSuccess : isSuccess,
    isError: isCmTable ? !cmTableEntity && isFallbackError : config.alwaysRequestRelatedItemsAndMedia ? l3IsError : isError,
    isFetchingRelationships,
    refresh,
    fetchRelationships,
    loadingProgress
  };
}

/**
 * Sidebar relationship grid panel visibility.
 *
 * User-controlled: opens when `showRelationShip` is set — either by a manual
 * "Find Related" click or by the config-driven auto-open above — and a manual
 * close ("Back to grid") keeps it closed until the next selection.
 */
export function useRelationshipPanelActive(): boolean {
  const { showRelationShip } = useSelectionSelector();
  return showRelationShip;
}

/**
 * Media strip / layout gate. Opens with the relationship panel, OR whenever
 * alwaysRequestRelatedItemsAndMedia is enabled and an entity is selected — so
 * the media drawer surfaces automatically under the config flag even before the
 * panel's own open state settles.
 */
export function useMediaDrawerRelationshipGate(): boolean {
  const { showRelationShip, selectedEntityId } = useSelectionSelector();
  const config = useConfig();
  return showRelationShip || (Boolean(config.alwaysRequestRelatedItemsAndMedia) && !!selectedEntityId);
}
